import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKey } from '../../types';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';

interface SEOHeadProps {
  titleKey?: TranslationKey;
  descriptionKey?: TranslationKey;
  title?: string;
  description?: string;
  noIndex?: boolean;
  additionalMeta?: Array<{ name: string; content: string }>;
}

export const SEOHead: React.FC<SEOHeadProps> = ({ 
  titleKey, 
  descriptionKey,
  title,
  description,
  noIndex = false,
  additionalMeta = []
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const location = useLocation();
  const canonicalUrl = `${window.location.origin}${location.pathname}`;
  
  // Get translated or direct title and create full page title
  const translatedTitle = title || (titleKey ? t(titleKey) : 'Page');
  const pageTitle = `${translatedTitle} | Invoice-Converter.com`;
  
  // Get translated or direct description
  const translatedDescription = description || (descriptionKey ? t(descriptionKey) : '');
  
  // Create alternate language URLs
  const alternateLanguages = ['en', 'de'].map(lang => ({
    lang,
    url: `${window.location.origin}/${lang}${location.pathname}`
  }));
  
  return (
    <Helmet
      key={location.pathname}
      defaultTitle="Invoice-Converter.com"
      htmlAttributes={{ lang: language }} // Set HTML lang attribute based on current language
    >
      <title>{pageTitle}</title>
      <meta name="description" content={translatedDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {noIndex && <meta name="robots" content="noindex" />}
      
      {/* Add hreflang tags for language alternatives */}
      {alternateLanguages.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      {/* Add x-default hreflang */}
      <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}/en${location.pathname}`} />
      
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={translatedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      
      {additionalMeta.map((meta, index) => (
        <meta key={index} name={meta.name} content={meta.content} />
      ))}
    </Helmet>
  );
};